import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useId, useState } from "react";
import { pageImageUrl, type FieldOverride } from "../lib/api";
import { roleBlock, type Me } from "../lib/me";
import { when } from "../lib/when";
import { percent } from "../lib/words";
import { RoleGroup } from "./RequireRole";
import "../theme/review.css";

export interface Candidate {
  value: string;
  /** The document that gave this value, so the choice names which reading is meant. */
  documentId: string | null;
  documentPath: string;
  documentName: string;
  page: number;
  confidence: number;
  box: { x: number; y: number; width: number; height: number } | null;
}

/**
 * Two documents disagreeing on the same field.
 *
 * Never resolved automatically. Both candidates are shown with the region of the page each
 * came from, cropped so the engineer compares the evidence rather than the assertion. The
 * decision is theirs and it is recorded as theirs.
 */
export function Mismatch({
  field, candidates, onResolve, me, chosen = null, busy = false, error = null,
}: {
  field: string;
  candidates: Candidate[];
  onResolve: (c: Candidate, note: string) => void;
  me: Me | null | undefined;
  /** The choice in force, if somebody has made one. */
  chosen?: FieldOverride | null;
  busy?: boolean;
  error?: string | null;
}) {
  const inForce = chosen
    ? candidates.findIndex((c) => c.value === chosen.value && (!chosen.document_id || c.documentId === chosen.document_id))
    : -1;
  const [picked, setPicked] = useState<number | null>(inForce >= 0 ? inForce : null);
  const [note, setNote] = useState("");
  const noteId = useId();
  const unchanged = picked === null || (chosen !== null && picked === inForce);

  return (
    <div className="mismatch">
      <div className="bar">
        <AlertTriangle size={16} aria-hidden="true" />
        Sources disagree on {field}. {chosen ? "A value has been chosen." : "Pick the correct value."}
      </div>
      <div className="candidates" role="radiogroup" aria-label={`Values for ${field}`}>
        {candidates.map((c, i) => (
          <div key={`${c.documentId ?? c.documentPath}:${c.value}`} className={`candidate ${picked === i ? "picked" : ""}`}
               role="radio" aria-checked={picked === i} tabIndex={0}
               onClick={() => setPicked(i)}
               onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setPicked(i); } }}>
            <div className="v">{c.value}</div>
            <div className="src">
              {c.documentName} · page {c.page} · {percent(c.confidence)} confidence
            </div>
            <Crop path={c.documentPath} page={c.page} box={c.box} />
          </div>
        ))}
      </div>
      {chosen && (
        <p className="chosen-line">
          <CheckCircle2 size={14} aria-hidden="true" style={{ verticalAlign: "-2px" }} />{" "}
          Chosen by <span className="mono">{chosen.by}</span> on {when(chosen.at)}:{" "}
          <span className="mono">{chosen.value}</span>
          {chosen.machine_value && chosen.machine_value !== chosen.value && (
            <> in place of <span className="mono">{chosen.machine_value}</span>, which the documents put first</>
          )}.
          {chosen.note && <><br />&ldquo;{chosen.note}&rdquo;</>}
        </p>
      )}
      <RoleGroup role="engineer" me={me} label={`Choose the value for ${field}`}>
      <div style={{ padding: "0 16px 16px", display: "grid", gap: 8 }}>
        <label htmlFor={noteId} style={{ fontSize: 13, fontWeight: 600 }}>Note (optional)</label>
        <textarea id={noteId} className="note" value={note} maxLength={2000} disabled={busy}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why this value, for example the nameplate or the test certificate for this unit" />
        {error && <p role="alert" style={{ color: "var(--color-fail)", fontSize: 13 }}>{error}</p>}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button disabled={!!roleBlock(me, "engineer") || busy || unchanged}
                  onClick={() => picked !== null && onResolve(candidates[picked], note)}>
            {busy ? "Saving..." : chosen ? "Change choice" : "Confirm choice"}
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            The choice is recorded against your name, with the value it replaced. The documents are not changed.
          </span>
        </div>
      </div>
      </RoleGroup>
    </div>
  );
}

/** Shows the region of the page the value came from, scaled so the box fills the frame. */
function Crop({ path, page, box }: { path: string; page: number; box: Candidate["box"] }) {
  if (!box) return <div className="crop"><span className="muted" style={{ fontSize: 12, padding: 8, display: "block" }}>Location not marked on the page</span></div>;
  // Zoom so the cited region is legible, then offset so it sits in view.
  const zoom = Math.min(6, 0.34 / Math.max(box.width, 0.02));
  return (
    <div className="crop">
      <img
        src={pageImageUrl(path, page)}
        alt=""
        style={{
          width: `${zoom * 100}%`,
          left: `${-box.x * zoom * 100 + 6}%`,
          top: `${-box.y * zoom * 100 + 10}%`,
        }}
      />
    </div>
  );
}
